<template>
  <el-card v-if="round" style="margin-top: 15px;">
    <div class="tab-header">
      <span>{{ round.round_number }}차 상세</span>
      <div class="round-actions">
        <el-button
          v-if="round.status === 'PREPARING'"
          type="primary"
          size="small"
          @click="handleStart"
        >
          작업 시작
        </el-button>
        <el-button
          v-if="round.status === 'IN_PROGRESS'"
          type="warning"
          size="small"
          @click="handleSubmitReview"
        >
          검토 요청
        </el-button>
        <el-button
          v-if="round.status === 'REVIEW'"
          type="success"
          size="small"
          @click="handleReview('ACCEPTED')"
        >
          승인
        </el-button>
        <el-button
          v-if="round.status === 'REVIEW'"
          type="warning"
          size="small"
          @click="handleReview('REVISION_NEEDED')"
        >
          수정필요
        </el-button>
        <el-button
          v-if="round.status === 'REVIEW'"
          type="danger"
          size="small"
          @click="handleReview('REJECTED')"
        >
          반려
        </el-button>
      </div>
    </div>

    <el-descriptions :column="3" border size="small">
      <el-descriptions-item label="회차">{{ round.round_number }}차</el-descriptions-item>
      <el-descriptions-item label="상태">
        <el-tag :type="getStatusType(round.status)" size="small">{{ round.status_display || round.status }}</el-tag>
      </el-descriptions-item>
      <el-descriptions-item label="결과">
        <el-tag v-if="round.result && round.result !== 'PENDING'" :type="getResultType(round.result)" size="small">
          {{ round.result_display || round.result }}
        </el-tag>
        <span v-else>-</span>
      </el-descriptions-item>
      <el-descriptions-item label="예상비용">{{ formatCurrency(round.estimated_cost) }}</el-descriptions-item>
      <el-descriptions-item label="실제비용">{{ formatCurrency(round.actual_cost) }}</el-descriptions-item>
      <el-descriptions-item label="검토자">{{ round.reviewer_name || '-' }}</el-descriptions-item>
    </el-descriptions>

    <!-- 회차별 이미지 -->
    <div class="section-header" style="margin-top: 20px;">
      <h4>이미지</h4>
      <el-upload
        :action="uploadUrl"
        :headers="uploadHeaders"
        name="image"
        :show-file-list="false"
        :on-success="handleUploadSuccess"
        :on-error="handleUploadError"
        :before-upload="beforeUpload"
        accept="image/*"
      >
        <el-button size="small" type="primary">이미지 추가</el-button>
      </el-upload>
    </div>

    <div v-if="images.length > 0" class="image-gallery">
      <div v-for="img in images" :key="img.id" class="image-item">
        <el-image
          :src="img.image"
          :preview-src-list="previewList"
          :initial-index="images.indexOf(img)"
          fit="cover"
          class="image-thumb"
        />
        <div class="image-info">
          <span v-if="img.caption" class="image-caption">{{ img.caption }}</span>
          <el-button
            type="danger"
            size="small"
            :icon="Delete"
            circle
            class="image-delete-btn"
            @click="handleDeleteImage(img)"
          />
        </div>
      </div>
    </div>
    <el-empty v-else description="등록된 이미지가 없습니다." :image-size="60" />

    <!-- 회차별 작업 -->
    <h4 style="margin-top: 15px;">작업 내역</h4>
    <el-table :data="round.works || []" border size="small" stripe>
      <el-table-column prop="sequence" label="순서" width="60" align="center" />
      <el-table-column prop="work_type_display" label="작업유형" width="120" />
      <el-table-column prop="worker_name" label="담당자" width="100" />
      <el-table-column prop="supplier_name" label="외주업체" width="120" />
      <el-table-column label="상태" width="90">
        <template #default="{ row }">
          <el-tag :type="getWorkStatusType(row.status)" size="small">{{ row.status_display || row.status }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="planned_start" label="예정시작" width="110" />
      <el-table-column prop="planned_end" label="예정종료" width="110" />
    </el-table>

    <!-- 검토 다이얼로그 -->
    <el-dialog v-model="showReviewDialog" title="회차 검토" width="400px" append-to-body>
      <el-form :model="reviewForm">
        <el-form-item label="코멘트">
          <el-input v-model="reviewForm.comments" type="textarea" :rows="3" placeholder="검토 코멘트" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showReviewDialog = false">취소</el-button>
        <el-button type="primary" @click="submitReview" :loading="submitting">확인</el-button>
      </template>
    </el-dialog>
  </el-card>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { Delete } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axiosInstance from '@/api/axios'

const props = defineProps({
  round: { type: Object, default: () => ({}) },
  sampleRequestId: { type: [String, Number], default: null }
})

const emit = defineEmits(['refresh'])

const showReviewDialog = ref(false)
const submitting = ref(false)
const reviewForm = reactive({ result: '', comments: '' })

// 이미지 관련
const images = computed(() => props.round?.images || [])
const previewList = computed(() => images.value.map(img => img.image))

const uploadUrl = computed(() => {
  const baseUrl = (axiosInstance.defaults.baseURL || '/api').replace(/\/$/, '')
  return `${baseUrl}/sample-rounds/${props.round?.id}/upload-image/`
})

const uploadHeaders = computed(() => {
  const token = localStorage.getItem('access_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
})

const beforeUpload = (file) => {
  const isImage = file.type.startsWith('image/')
  const isLt10M = file.size / 1024 / 1024 < 10
  if (!isImage) {
    ElMessage.error('이미지 파일만 업로드 가능합니다.')
    return false
  }
  if (!isLt10M) {
    ElMessage.error('파일 크기는 10MB 이하여야 합니다.')
    return false
  }
  return true
}

const handleUploadSuccess = () => {
  ElMessage.success('이미지가 업로드되었습니다.')
  emit('refresh')
}

const handleUploadError = () => {
  ElMessage.error('이미지 업로드에 실패했습니다.')
}

const handleDeleteImage = async (img) => {
  try {
    await ElMessageBox.confirm('이미지를 삭제하시겠습니까?', '이미지 삭제', { type: 'warning' })
    await axiosInstance.delete(`sample-rounds/${props.round.id}/delete-image/${img.id}/`)
    ElMessage.success('이미지가 삭제되었습니다.')
    emit('refresh')
  } catch (error) {
    if (error !== 'cancel') ElMessage.error(error.response?.data?.error || '삭제 실패')
  }
}

const getStatusType = (status) => {
  const map = { PREPARING: 'info', IN_PROGRESS: '', REVIEW: 'warning', COMPLETED: 'success', CANCELLED: 'danger' }
  return map[status] || 'info'
}

const getResultType = (result) => {
  const map = { ACCEPTED: 'success', REVISION_NEEDED: 'warning', REJECTED: 'danger' }
  return map[result] || 'info'
}

const getWorkStatusType = (status) => {
  const map = { PENDING: 'info', IN_PROGRESS: '', COMPLETED: 'success', HOLD: 'warning' }
  return map[status] || 'info'
}

const formatCurrency = (amount) => {
  if (!amount) return '-'
  return Number(amount).toLocaleString() + '원'
}

const handleStart = async () => {
  try {
    await axiosInstance.post(`sample-rounds/${props.round.id}/start/`)
    ElMessage.success('작업이 시작되었습니다.')
    emit('refresh')
  } catch (error) {
    ElMessage.error(error.response?.data?.error || '처리 실패')
  }
}

const handleSubmitReview = async () => {
  try {
    await ElMessageBox.confirm('검토를 요청하시겠습니까?', '검토 요청')
    await axiosInstance.post(`sample-rounds/${props.round.id}/submit-review/`)
    ElMessage.success('검토 요청되었습니다.')
    emit('refresh')
  } catch (error) {
    if (error !== 'cancel') ElMessage.error(error.response?.data?.error || '처리 실패')
  }
}

const handleReview = (result) => {
  reviewForm.result = result
  reviewForm.comments = ''
  showReviewDialog.value = true
}

const submitReview = async () => {
  submitting.value = true
  try {
    await axiosInstance.post(`sample-rounds/${props.round.id}/review/`, {
      result: reviewForm.result,
      comments: reviewForm.comments
    })
    ElMessage.success('검토가 완료되었습니다.')
    showReviewDialog.value = false
    emit('refresh')
  } catch (error) {
    ElMessage.error(error.response?.data?.error || '처리 실패')
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}
.tab-header span {
  font-size: 16px;
  font-weight: 600;
}
.round-actions {
  display: flex;
  gap: 5px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.section-header h4 {
  margin: 0;
}

.image-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 10px;
}
.image-item {
  position: relative;
  width: 150px;
}
.image-thumb {
  width: 150px;
  height: 150px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  cursor: pointer;
}
.image-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
}
.image-caption {
  font-size: 12px;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}
.image-delete-btn {
  flex-shrink: 0;
}
</style>
